import React, { useRef, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import CMSSelectPreviewConfiguration from './CMSSelectPreviewConfiguration';
import { useQuery, useMutation } from '@apollo/client';
import Client from '../../client/Client';
import { Button, SwipeableDrawer } from '@material-ui/core';
import { useStore } from '../../store/storeUtils';
import '../../scss/common/sidebar.scss';
import '../../scss/neurosales/neurosales.scss';
import CMSButton from '../generic/inputs/CMSButton';
import { toJS } from 'mobx';
import CMSMenuNotAvailable from './CMSMenuNotAvailable';
import Loader from './../common/Loader';
import { getValidatorComponent } from '../../validators/componentsValidators';
import CMSSnackbar from '../generic/inputs/CMSSnackbar';
import LoaderRef from '../common/LoaderRef';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorSidebar from './ErrorSiderbar';
import SimpleModal from '../common/SimpleModal/SimpleModal';

// Lazy import dei componenti per boost performance
const HomeNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/HomeNeurosales'));
const VideoNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/VideoNeurosales'));
const VirtualPhotoNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/VirtualPhotoNeurosales'));
const InteriorExperienceNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/InteriorExperienceNeurosales'));
const SliderGalleryNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/SliderGalleryNeurosales'));
const VirtualTourNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/VirtualTourNeurosales'));
const ServiziNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/ServiziNeurosales'));
const NoComponentNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/NoComponentNeurosales'));
const ComponentSwitcherNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/ComponentSwitcherNeurosales'));
const FloorplanningNeurosales = /*#__PURE__*/React.lazy(() => import('./components/pages/FloorplanningNeurosales'));
const CMSNeurosales = observer(props => {
  const cmsSnackbarRef = useRef(null);
  const loaderRef = useRef(null);
  const store = useStore();
  const containerRef = useRef(null);
  const [showAnnullaModal, setShowAnnullaModal] = useState(false);
  const [validatorErrors, setValidatorErrors] = useState([]);
  const [createPreviewConfiguration] = useMutation(Client.CREATE_PREVIEW_CONFIGURATION_MUTATION);
  const [loading, setLoading] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  useEffect(() => {
    const mustLoad = document.getElementsByClassName('loading-count').length > 0;
    if (!intervalId && mustLoad) {
      const newIntervalId = setInterval(() => {
        const mustLoad = document.getElementsByClassName('loading-count').length > 0;
        setLoading(mustLoad);
        if (!mustLoad) {
          clearInterval(newIntervalId);
          setIntervalId(null);
        }
      }, 100);
      setIntervalId(newIntervalId);
    }
    // eslint-disable-next-line
  }, [store.loaderCount]);
  const setPreviewConfiguration = configuration => {
    store.setRenderPreviewConfiguration(configuration);
    store.synchronizeWholeConfiguration();
  };
  const prjInfo = useQuery(Client.GET_PROJECT_INFO, Client.GET_PROJECT_INFO_DEFAULT_OPTIONS(props.hostname, props.projectInfo ? true : false));
  if (prjInfo && !prjInfo.loading && prjInfo.called && prjInfo.data && prjInfo.data.getProjectInfoByHost) {
    store.setProjectInfo({
      ...prjInfo.data.getProjectInfoByHost
    });
  }
  const closeModal = () => {
    props.setCmsModal(false);
  };
  const getVirtualPhotoGenericError = validationErrors => {
    // Recupero le categorie dai messaggi d'errore
    let errorCategories = validationErrors.map(e => e.message.slice(e.message.indexOf('"') + 1, e.message.lastIndexOf('"'))); // rimuovo eventuali duplicati
    // Rimuovo i duplicati
    errorCategories = [...new Set(errorCategories)];
    const error = [`Non è possibile salvare, sono presenti degli errori ${errorCategories.length > 1 ? 'nelle categorie' : 'nella categoria'} ${errorCategories.join(', ')}.`];
    return error;
  };
  const saveConfiguration = (closeSidebar = false) => {
    const validationErrors = validateConfigs();
    setValidatorErrors(validationErrors);
    let errors = [];
    if (validationErrors.length > 0) {
      // Gestione errori VirtualPhoto
      if (currentComponent.component === 'VirtualPhoto') {
        if (store.virtualPhotoCurrentCategory) {
          // Se ho valorizzata la virtualPhotoCurrentCategory, mostro solo i messaggi ineerenti a quella categoria
          errors = validationErrors.map(e => e.message).filter(el => el.toLowerCase().includes(`"${store.virtualPhotoCurrentCategory.toLowerCase()}"`));

          // Se non sono presenti errori per la categoria su cui sto lavorando, mostro il messaggio generico
          if (errors.length === 0) {
            errors = getVirtualPhotoGenericError(validationErrors);
          }
        } else {
          // Se non sono in una categoria, mostro un errore generico.
          errors = getVirtualPhotoGenericError(validationErrors);
        }
      } else {
        errors = validationErrors.map(e => e.message);
      }
      if (errors.length > 0) {
        cmsSnackbarRef.current.showSnackbar('error', errors);
      }
    } else {
      savePreviewConfiguration(toJS(store.previewConfiguration), closeSidebar);
    }
  };
  const annullaConfiguration = () => {
    closeModal();
    setValidatorErrors([]);
    store.setPreviewConfiguration(store.getRenderPreviewConfiguration());
  };
  let currentComponent = null;
  let componentsPath = [];
  if (store.previewConfiguration) {
    componentsPath = store.getComponentFromConfiguration(props.path);
    currentComponent = componentsPath[componentsPath.length - 1];
  }
  const getValidator = useCallback(currentComponent => {
    return getValidatorComponent(currentComponent, props.path);
  }, [props]);
  const validateConfigs = useCallback(() => {
    const config = toJS(props.path === '' ? store.previewConfiguration.configuration : currentComponent);
    const validator = getValidator(currentComponent);
    let errors = [];
    if (validator) {
      const validateResult = validator.validate(config, {
        abortEarly: false
      });
      if (validateResult.error && validateResult.error.details) {
        validateResult.error.details.forEach(e => {
          let errorSplit = JSON.parse(e.message);
          errors.push(errorSplit);
          console.error(props.path === '' ? 'HomePage' : currentComponent.component, e.message);
        });
      }
    }
    return errors;
  }, [currentComponent, getValidator, props.path, store.previewConfiguration]);
  const menuMap = {
    HomePage: /*#__PURE__*/React.createElement(HomeNeurosales, {
      containerRef: containerRef,
      component: currentComponent,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    Video: /*#__PURE__*/React.createElement(VideoNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    VirtualPhoto: /*#__PURE__*/React.createElement(VirtualPhotoNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    InteriorExperience: /*#__PURE__*/React.createElement(InteriorExperienceNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    SliderGallery: /*#__PURE__*/React.createElement(SliderGalleryNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    VirtualTour: /*#__PURE__*/React.createElement(VirtualTourNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    Servizi: /*#__PURE__*/React.createElement(ServiziNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    ComponentSwitcher: /*#__PURE__*/React.createElement(ComponentSwitcherNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    InteriorExperienceSplit: /*#__PURE__*/React.createElement(ComponentSwitcherNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    Floorplanning: /*#__PURE__*/React.createElement(FloorplanningNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    }),
    NoComponent: /*#__PURE__*/React.createElement(NoComponentNeurosales, {
      component: currentComponent,
      containerRef: containerRef,
      componentsPath: componentsPath,
      validatorErrors: validatorErrors
    })
  };
  const savePreviewConfiguration = (previewConfiguration, closeSidebar = false) => {
    // save on db
    let navigationDuplicate;
    previewConfiguration && previewConfiguration.configuration.homePage.boxes.forEach(comp => {
      previewConfiguration.configuration.homePage.boxes.forEach(c => {
        if (comp.navigation === c.navigation && comp.title !== c.title) {
          navigationDuplicate = 'nella tile ' + comp.title + ' e nella tile ' + c.title;
        }
      });
    });
    if (navigationDuplicate) {
      cmsSnackbarRef.current.showSnackbar('error', 'Le url di navigazione ' + navigationDuplicate + ' coincidono', 5000);
    } else {
      loaderRef.current.showLoader();
      createPreviewConfiguration(Client.CREATE_PREVIEW_CONFIGURATION_OPTIONS(store.projectId, previewConfiguration)).then(e => {
        loaderRef.current.hideLoader();
        cmsSnackbarRef.current.showSnackbar('success', 'Le modifiche sono state salvate correttamente', 5000);
        setPreviewConfiguration(previewConfiguration);
        if (closeSidebar) {
          closeModal();
        }
        if (props && props.forceUpdate) {
          console.log('update view');
          props.forceUpdate();
        }
      }).catch(e => {
        loaderRef.current.hideLoader();
        // errore con snackbar
        cmsSnackbarRef.current.showSnackbar('error', 'Si è verificato un errore durante il salvataggio della configurazione.', 5000);
        console.log(e);
      });
    }
  };
  const checkClose = () => {
    if (JSON.stringify(store.previewConfiguration) === JSON.stringify(store.renderPreviewConfiguration)) {
      // nessuna modifica, chiudo semplicemente
      closeModal();
    } else {
      console.log(toJS(store.previewConfiguration));
      console.log(toJS(store.renderPreviewConfiguration));
      // solo se ci sono modifiche mostro modale
      setShowAnnullaModal(true);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SwipeableDrawer, {
    onClose: () => {
      checkClose();
    },
    onOpen: () => {},
    className: "cms-container",
    anchor: 'right',
    classes: {
      paperAnchorRight: 'cms-sidebar'
    },
    open: props.cmsModal
  }, /*#__PURE__*/React.createElement("div", {
    className: "cms-sidebar-content"
  }, /*#__PURE__*/React.createElement(LoaderRef, {
    whiteLoader: true,
    ref: loaderRef
  }), /*#__PURE__*/React.createElement(SimpleModal, {
    onCancel: () => setShowAnnullaModal(false),
    showModal: showAnnullaModal,
    title: "Attenzione",
    subtitle: `Confermi di voler tornare indietro perdendo tutti i parametri impostati?`,
    onSubmit: () => {
      setShowAnnullaModal(false);
      annullaConfiguration();
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "cms-sidebar-body-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cms-sidebar-header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cms-sidebar-close-button"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      checkClose();
    },
    className: "close-button"
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon cross-black"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "cms-sidebar-body",
    ref: containerRef
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    FallbackComponent: ErrorSidebar
  }, store.previewConfiguration ? /*#__PURE__*/React.createElement(React.Suspense, {
    fallback: /*#__PURE__*/React.createElement(Loader, {
      whiteLoader: true,
      affectSave: false
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "cms-full-height"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cms-full-height"
  }, currentComponent ? props.path === '' ? menuMap['HomePage'] : menuMap[currentComponent.component] ? menuMap[currentComponent.component] : /*#__PURE__*/React.createElement(CMSMenuNotAvailable, {
    componentName: currentComponent.component
  }) : /*#__PURE__*/React.createElement(CMSMenuNotAvailable, null)))) : /*#__PURE__*/React.createElement(CMSSelectPreviewConfiguration, {
    defaultConfiguration: props.defaultConfiguration,
    localDevelopment: props.localDevelopment
  }))), currentComponent && (menuMap[currentComponent.component] || props.path === '') ? /*#__PURE__*/React.createElement("div", {
    className: "cms-action-buttons-container"
  }, /*#__PURE__*/React.createElement(CMSButton, {
    inverted: true,
    onClick: () => {
      checkClose();
    }
  }, "Annulla"), /*#__PURE__*/React.createElement(CMSButton, {
    disabled: store.uploading || loading,
    onClick: () => {
      saveConfiguration();
    }
  }, "Salva")) : null)), /*#__PURE__*/React.createElement(CMSSnackbar, {
    ref: cmsSnackbarRef
  })));
});
export default CMSNeurosales;